import React from 'react';
import { Button, Card, Flex, Row, Col, Typography, message, Switch, Select, Spin, Input } from 'antd';
import { ChevronDown, Save } from 'lucide-react';
import { CarouselVersionType, ScrollType, ThemeType, WidgetSettings } from '../../../types/widget-settings';
import { useWidgetSettings } from '../hooks/settings/use-widget-settings';
import { useWidgetContext } from '../context/widget';

const SETTINGS_LABEL_STYLE: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 15,
  color: '#2e2e2e'
};

export const SettingsPage: React.FC<{ widgetId: string }> = ({ widgetId }) => {
  const { saveSettings, getSettings } = useWidgetSettings({ widgetId });
  const { updateWidget } = useWidgetContext();
  const [handleError, setHandleError] = React.useState(false);

  const [isSaving, setIsSaving] = React.useState(false);
  const [settings, setSettings] = React.useState<WidgetSettings>({
    type: 'STANDARD',
    title: '',
    handle: '',
    avatar: '',
    hideAvatar: false,
    hideTitle: false,
    hideArrows: true,
    scrollDirection: 'horizontal',
    theme: 'light',
    carouselVersion: 'v1',
    notificationEmail: ''
  });

  const getWidgetSettings = React.useCallback(async () => {
    const res = await getSettings();
    if (res && 'type' in res) {
      setSettings({
        type: res.type,
        title: res.title ?? '',
        handle: res.handle ?? '',
        avatar: res.avatar ?? '',
        hideAvatar: !!res.hideAvatar,
        hideTitle: !!res.hideTitle,
        hideArrows: !!res.hideArrows,
        scrollDirection: (res.scrollDirection as ScrollType) || 'horizontal',
        theme: (res.theme as ThemeType) || 'light',
        carouselVersion: (res.carouselVersion as CarouselVersionType) || 'v1',
        notificationEmail: res.notificationEmail || ''
      });
    } else {
      message.error('No se pudo obtener la configuración. Intenta nuevamente.');
    }
  }, [getSettings]);

  const saveWidgetSettings = async () => {
    setIsSaving(true);
    try {
      const res = await saveSettings(settings);
      if (res) {
        message.success('Configuración guardada correctamente');

        updateWidget(widgetId, {
          title: settings.title,
          handle: settings.handle
        });
      } else {
        message.error('No se pudo guardar la configuración. Intenta nuevamente.');
      }
    } catch (error) {
      console.error('Error al guardar la configuración:', error);
      message.error('No se pudo guardar la configuración. Intenta nuevamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = (key: keyof typeof settings) => (checked: boolean) => {
    setSettings(prev => ({ ...prev, [key]: !checked }));
  };

  React.useEffect(() => {
    getWidgetSettings();
  }, [getWidgetSettings]);

  return (
    <Flex justify='center'>
      <Row gutter={[16, 16]} style={{ width: '100%', maxWidth: 700 }}>
        <Col span={24} style={{ marginBottom: '20px' }}>
          <Typography.Title level={2} style={{ textAlign: 'center', marginBottom: '30px' }}>
            Configuración del widget
          </Typography.Title>

          {/* Título (editable) */}
          <Card>
            <Flex vertical gap='small'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Título del widget</Typography.Text>
              <Input
                placeholder='Nombre visible del widget'
                value={settings.title}
                onChange={e => setSettings(prev => ({ ...prev, title: e.target.value }))}
                style={{ maxWidth: 350, backgroundColor: '#f5f5f5', textAlign: 'center', margin: '0 auto' }}
              />
              <Typography.Text type='secondary' style={{ fontSize: 12 }}>
                Este título se usa como nombre visible/identificador legible del widget.
              </Typography.Text>
            </Flex>
          </Card>

          {/* Handle (editable) */}
          <Card style={{ marginTop: 16 }}>
            <Flex vertical gap='small'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Cuenta de Instagram</Typography.Text>
              <Input
                addonBefore='@'
                placeholder='Usuario de Instagram'
                value={settings.handle}
                status={handleError ? 'error' : undefined}
                onChange={e => {
                  const raw = e.target.value;

                  if (/https?:\/\//i.test(raw) || /\.com/i.test(raw)) {
                    setHandleError(true);
                    message.warning('No ingreses un link, solo el usuario de Instagram');
                    return;
                  }

                  const regex = /^[a-zA-Z0-9._]{0,30}$/;
                  if (raw === '' || regex.test(raw)) {
                    setSettings(prev => ({ ...prev, handle: raw }));
                    setHandleError(false);
                  } else {
                    setHandleError(true);
                    message.error(
                      'El usuario solo puede tener letras, números, puntos y guiones bajos (máx 30 caracteres)'
                    );
                  }
                }}
                className='handle-input'
                style={{
                  maxWidth: 350,
                  backgroundColor: '#f5f5f5',
                  textAlign: 'center',
                  margin: '0 auto'
                }}
              />

              <style>
                {`
                  .handle-input .ant-input {
                    text-align: center;
                  }
                  .handle-input .ant-input:not(:placeholder-shown) {
                    text-indent: -32px;
                  }
                  .handle-input .ant-input::placeholder {
                    text-align: center;
                  }
                `}
              </style>

              <Typography.Text type='secondary' style={{ fontSize: 12 }}>
                Cuenta de Instagram del widget (Ejemplo: drupi.io).
              </Typography.Text>
            </Flex>
          </Card>

          {/* Switches */}
          <Card style={{ marginTop: 16 }}>
            <Flex align='center' justify='space-between' gap='middle'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Mostrar Avatar</Typography.Text>
              <Switch checked={!settings.hideAvatar} onChange={handleToggle('hideAvatar')} />
            </Flex>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <Flex align='center' justify='space-between' gap='middle'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Mostrar Título (username)</Typography.Text>
              <Switch checked={!settings.hideTitle} onChange={handleToggle('hideTitle')} />
            </Flex>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <Flex align='center' justify='space-between' gap='middle'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Mostrar flechas de navegación en carousel</Typography.Text>
              <Switch checked={!settings.hideArrows} onChange={handleToggle('hideArrows')} />
            </Flex>
          </Card>

          {/* Selectores */}
          <Card style={{ marginTop: 16 }}>
            <Flex align='center' justify='space-between' gap='middle'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Dirección del scroll (modal)</Typography.Text>
              <Select
                value={settings.scrollDirection}
                onChange={value => setSettings(prev => ({ ...prev, scrollDirection: value }))}
                style={{ width: 140 }}
                suffixIcon={<ChevronDown width={12} />}
                className='custom-select'
              >
                <Select.Option value='horizontal'>Horizontal</Select.Option>
                <Select.Option value='vertical'>Vertical</Select.Option>
              </Select>
              <style>
                {`
                  .custom-select .ant-select-selector {
                    border: 1px solid rgba(138, 148, 166, 1) !important;
                    border-radius: 8px;
                  }
                `}
              </style>
            </Flex>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <Flex align='center' justify='space-between' gap='middle'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Theme</Typography.Text>
              <Select
                value={settings.theme}
                onChange={value => setSettings(prev => ({ ...prev, theme: value }))}
                style={{ width: 140 }}
                suffixIcon={<ChevronDown width={12} />}
                className='custom-select'
              >
                <Select.Option value='light'>Light</Select.Option>
                <Select.Option value='dark'>Dark</Select.Option>
              </Select>
              <style>
                {`
                  .custom-select .ant-select-selector {
                    border: 1px solid rgba(138, 148, 166, 1) !important;
                    border-radius: 8px;
                  }
                `}
              </style>
            </Flex>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <Flex align='center' justify='space-between' gap='middle'>
              <Typography.Text style={SETTINGS_LABEL_STYLE}>Versión del carrusel</Typography.Text>
              <Select
                value={settings.carouselVersion}
                onChange={value => setSettings(prev => ({ ...prev, carouselVersion: value }))}
                style={{ width: 140 }}
                suffixIcon={<ChevronDown width={12} />}
                className='custom-select'
              >
                <Select.Option value='v1'>V1</Select.Option>
                <Select.Option value='v2'>V2</Select.Option>
              </Select>
              <style>
                {`
                  .custom-select .ant-select-selector {
                    border: 1px solid rgba(138, 148, 166, 1) !important;
                    border-radius: 8px;
                  }
                `}
              </style>
            </Flex>
          </Card>

          {/* Guardar */}
          <Button
            onClick={saveWidgetSettings}
            icon={isSaving ? <Spin size='small' /> : <Save width={15} />}
            style={{
              width: '200px',
              height: '45px',
              marginTop: '32px',
              fontSize: '14px',
              fontWeight: 600
            }}
          >
            {isSaving ? 'Guardando...' : 'Guardar Configuración'}
          </Button>
        </Col>
      </Row>
    </Flex>
  );
};
